/**
 * ORDER POPUP COMPONENT - SMART UPSELL SYSTEM
 * 
 * Full-screen, two-step popup flow with psychological triggers:
 * - Step 1: Warehouse stock verification animation
 * - Step 2: Order summary with inline "Searching for Deals" animation in upsell section + 15-min timer
 * 
 * Upsell Logic (3-step flow):
 * - Upsell 1: Pillowcase Set ($19/$29 - with +/- quantity selector)
 * - Upsell 2: Silk Eyemask ($29 instead of $49 - 41% off)
 * - Upsell 3: Extra Pillow ($39.90/$49.90 - bundle special)
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { 
  X, Check, ChevronDown, ChevronUp, Gift, Shield, Truck, 
  MapPin, Clock, Package, Sparkles, Search, Zap, Star, Info, Lock
} from "lucide-react";
import { generateCheckoutUrl, type UpsellType } from "@/lib/shopifyCheckout";
// ATC tracking removed from OrderPopup — tracked by landing page CTA + Shopify checkout only
import { StockInventoryBar } from "@/components/StockInventoryBar";
import { GiftProgressBar, getActiveTiers, buildGiftTiers } from "@/components/GiftProgressBar";
import type { GiftTier } from "@/components/GiftProgressBar";
import { ShippingMessage } from "@/components/ShippingMessage";
import { useGeolocation, isUSVisitor, useNonUsBadgeText, formatLocationText } from "@/hooks/useGeolocation";
import { useLocalePricing } from "@/hooks/useLocalePricing";
import {
  trackViewPromotion, 
  trackSelectPromotion, 
  // trackAddToCart removed — ATC tracked by landing page CTA + Shopify checkout only
  trackRemoveFromCart,
  trackBeginCheckout,
  createUpsellItem,
  createUpsellPromotion,
  createMainProductItem,
  GA4_PRODUCT_IDS
} from "@/lib/ga4-tracking";
import { getUpsellOrder, type UpsellProductId } from '@shared/upsellOrder';

interface OrderPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onExitRecovery?: () => void; // Called when user closes checkout without completing
  selectedSize: "Standard" | "King";
  selectedQuantity: number;
  stockLevel: number;
  countdown: { days?: number; hours: number; minutes: number; seconds: number };
  pageSlug?: string; // Current page slug for UTM campaign tracking
  hideFirstUpsell?: boolean; // Hide the first upsell (pillowcases)
  hideSecondUpsell?: boolean; // Hide the second upsell (silk eyemask)
  hideThirdUpsell?: boolean; // Hide the third upsell (extra pillow)
}

interface Warehouse {
  id: string;
  name: string;
  location: string;
  status: "checking" | "unavailable" | "available";
}

interface UserLocation {
  city: string | null;
  country: string | null;
  countryCode: string | null;
  state: string | null;
}

interface SmartUpsell {
  id: string;
  name: string;
  headline: string;
  description: string;
  whyItMakesSense: string[];
  price: number;
  originalPrice: number;
  savings: number;
  savingsPercent: number;
  image: string;
  imageSrcSet?: string;
  badge?: string;
  limitedQty?: number;
}

// Base warehouses - the last one will be dynamically set based on user location
const baseWarehouses: Omit<Warehouse, 'status'>[] = [
  { id: "nj", name: "New Jersey DC", location: "Newark, NJ" },
  { id: "tx", name: "Texas Fulfillment", location: "Dallas, TX" },
];

// Location to warehouse mapping
const locationWarehouseMap: Record<string, { name: string; location: string }> = {
  "America/New_York": { name: "Northeast Distribution", location: "New York, NY" },
  "America/Chicago": { name: "Midwest Fulfillment", location: "Chicago, IL" },
  "America/Denver": { name: "Mountain Region DC", location: "Denver, CO" },
  "America/Los_Angeles": { name: "West Coast Hub", location: "Los Angeles, CA" },
  "America/Phoenix": { name: "Southwest Center", location: "Phoenix, AZ" },
  "America/Toronto": { name: "Canada Distribution", location: "Toronto, ON" },
  "America/Vancouver": { name: "Pacific Northwest DC", location: "Vancouver, BC" },
  "Europe/London": { name: "UK Fulfillment", location: "London, UK" },
  "Europe/Paris": { name: "EU Distribution", location: "Paris, FR" },
  "Europe/Berlin": { name: "Central EU Hub", location: "Berlin, DE" },
  "Australia/Sydney": { name: "Australia Center", location: "Sydney, AU" },
};

// Pricing now comes from useLocalePricing() hook (locale-aware)

// Country flag mapping
const countryFlags: Record<string, string> = {
  US: "🇺🇸", CA: "🇨🇦", GB: "🇬🇧", UK: "🇬🇧", AU: "🇦🇺", DE: "🇩🇪", FR: "🇫🇷",
  IT: "🇮🇹", ES: "🇪🇸", NL: "🇳🇱", BE: "🇧🇪", SE: "🇸🇪", NO: "🇳🇴", DK: "🇩🇰",
  FI: "🇫🇮", AT: "🇦🇹", CH: "🇨🇭", IE: "🇮🇪", NZ: "🇳🇿", JP: "🇯🇵", KR: "🇰🇷",
  SG: "🇸🇬", HK: "🇭🇰", MX: "🇲🇽", BR: "🇧🇷", AR: "🇦🇷", CL: "🇨🇱", CO: "🇨🇴",
};

// Timezone to location mapping
const timezoneMap: Record<string, { city: string; country: string; countryCode: string; state: string }> = {
  "America/New_York": { city: "New York", country: "United States", countryCode: "US", state: "NY" },
  "America/Chicago": { city: "Chicago", country: "United States", countryCode: "US", state: "IL" },
  "America/Denver": { city: "Denver", country: "United States", countryCode: "US", state: "CO" },
  "America/Los_Angeles": { city: "Los Angeles", country: "United States", countryCode: "US", state: "CA" },
  "America/Phoenix": { city: "Phoenix", country: "United States", countryCode: "US", state: "AZ" },
  "America/Toronto": { city: "Toronto", country: "Canada", countryCode: "CA", state: "ON" },
  "America/Vancouver": { city: "Vancouver", country: "Canada", countryCode: "CA", state: "BC" },
  "Europe/London": { city: "London", country: "United Kingdom", countryCode: "GB", state: "" },
  "Europe/Paris": { city: "Paris", country: "France", countryCode: "FR", state: "" },
  "Europe/Berlin": { city: "Berlin", country: "Germany", countryCode: "DE", state: "" },
  "Australia/Sydney": { city: "Sydney", country: "Australia", countryCode: "AU", state: "NSW" },
};

// Deal search messages for inline animation
const dealSearchMessages = [
  { text: "Analyzing your order...", icon: Search },
  { text: "Checking bundle combinations...", icon: Package },
  { text: "Finding exclusive discounts...", icon: Zap },
  { text: "Deal found!", icon: Gift },
];

// Upsell builders now use locale-aware pricing from useLocalePricing()
// Moved inside component to access hook data

export default function OrderPopup({ 
  isOpen, 
  onClose,
  onExitRecovery,
  selectedSize, 
  selectedQuantity,
  stockLevel,
  countdown,
  pageSlug = '',
  hideFirstUpsell = false,
  hideSecondUpsell = false,
  hideThirdUpsell = false,
}: OrderPopupProps) {
  
  const { fp, fpc, fpInt, locale, isInternational, currencyCode: localeCurrencyCode, standardPricing, kingPricing, getUpsellPillow, getUpsellPillowcase, getUpsellEyemask, getShippingCost: localeShippingCost, insuranceCost: localeInsuranceCost, getBonusPillowcaseValue, giftTiers } = useLocalePricing();
  const [step, setStep] = useState<1 | 2>(1);
  const [warehouseStatuses, setWarehouseStatuses] = useState<Record<string, "checking" | "unavailable" | "available">>({});
  const [foundWarehouse, setFoundWarehouse] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<UserLocation>({ city: null, country: null, countryCode: null, state: null });
  const [selectedUpsell, setSelectedUpsell] = useState<boolean>(false); // First upsell: pillowcase set
  const [selectedSecondUpsell, setSelectedSecondUpsell] = useState<boolean>(false); // Second upsell: silk eyemask
  const [selectedThirdUpsell, setSelectedThirdUpsell] = useState<boolean>(false); // Third upsell: extra pillow
  const [pillowcaseQuantity, setPillowcaseQuantity] = useState<number>(1); // Default to 1, will be set to 2 for 4-pillow orders in the open effect
  const [slipperSize, setSlipperSize] = useState<'S/M' | 'L/XL'>('L/XL'); // Default slipper size for gift
  const [firstUpsellCollapsed, setFirstUpsellCollapsed] = useState<boolean>(false); // Collapse animation state for first upsell
  const [secondUpsellCollapsed, setSecondUpsellCollapsed] = useState<boolean>(false); // Collapse animation state for second upsell
  const [thirdUpsellCollapsed, setThirdUpsellCollapsed] = useState<boolean>(false); // Collapse animation state for third upsell
  const [showSecondUpsell, setShowSecondUpsell] = useState<boolean>(false); // Controls second upsell visibility (stays true once shown)
  const [secondUpsellRevealing, setSecondUpsellRevealing] = useState<boolean>(false); // Animation state for second upsell reveal
  const [showThirdUpsell, setShowThirdUpsell] = useState<boolean>(false); // Controls third upsell visibility
  const [thirdUpsellRevealing, setThirdUpsellRevealing] = useState<boolean>(false); // Animation state for third upsell reveal
  const [shippingInsurance, setShippingInsurance] = useState<boolean>(true); // Pre-checked by default
  const [expandedDetails, setExpandedDetails] = useState<boolean>(false);
  const [expandedSecondDetails, setExpandedSecondDetails] = useState<boolean>(false);
  const [expandedThirdDetails, setExpandedThirdDetails] = useState<boolean>(false);
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [warehouses, setWarehouses] = useState<Omit<Warehouse, 'status'>[]>([]);
  
  // Inline deal search state
  const [dealSearchActive, setDealSearchActive] = useState(true);
  const [dealSearchIndex, setDealSearchIndex] = useState(0);
  const [dealSearchComplete, setDealSearchComplete] = useState(false);
  
  const [dealTimer, setDealTimer] = useState({ minutes: 15, seconds: 0 });
  
  // Viewing and purchased today state (matching product form)
  const [viewerCount, setViewerCount] = useState(46);
  const [displayedViewerCount, setDisplayedViewerCount] = useState(46);
  const [isViewerCountAnimating, setIsViewerCountAnimating] = useState(false);
  const [purchasedToday, setPurchasedToday] = useState(50);
  const [displayedPurchasedToday, setDisplayedPurchasedToday] = useState(50);
  const [isPurchasedAnimating, setIsPurchasedAnimating] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isFirstUpsellAnimating, setIsFirstUpsellAnimating] = useState(false); // Lock to prevent double-click
  const [isSecondUpsellAnimating, setIsSecondUpsellAnimating] = useState(false); // Lock to prevent double-click
  const [isThirdUpsellAnimating, setIsThirdUpsellAnimating] = useState(false); // Lock to prevent double-click
  
  const scrollPositionRef = useRef(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const hasOpenedBeforeRef = useRef(false); // Track if checkout was opened before (skip animation on reopen)
  const warehouseInitializedRef = useRef(false);
  const lastClosedQuantityRef = useRef<number>(selectedQuantity); // Prevent warehouse setup from running more than once per popup open
  const animationTimeoutsRef = useRef<NodeJS.Timeout[]>([]); // Track animation timeouts for cleanup
  
  // Persisted state refs (to restore on reopen)
  const persistedUpsellRef = useRef<boolean>(false);
  const persistedSecondUpsellRef = useRef<boolean>(false);
  const persistedThirdUpsellRef = useRef<boolean>(false);
  const persistedShippingInsuranceRef = useRef<boolean>(true);
  const persistedFirstUpsellCollapsedRef = useRef<boolean>(false);
  const persistedSecondUpsellCollapsedRef = useRef<boolean>(false);
  const persistedThirdUpsellCollapsedRef = useRef<boolean>(false);
  const persistedShowSecondUpsellRef = useRef<boolean>(false);
  const persistedShowThirdUpsellRef = useRef<boolean>(false);
  const persistedPillowcaseQuantityRef = useRef<number>(1);

  // Get pricing based on size (locale-aware from useLocalePricing)
  const getPricing = (size: "Standard" | "King") => size === "Standard" ? standardPricing : kingPricing;
  const currentPricing = getPricing(selectedSize);
  const selectedPricing = currentPricing.find(p => p.quantity === selectedQuantity) || currentPricing[0];

  // Get all upsells (locale-aware)
  const upsellPillowPricing = getUpsellPillow(selectedSize);
  const upsellPillowcasePricing = getUpsellPillowcase(selectedSize);
  const upsellEyemaskPricing = getUpsellEyemask;
  
  const firstUpsell: SmartUpsell = {
    id: "pillowcase-set",
    name: "MICRO-INFUSE® Extra Month",
    headline: "Extend Your Treatment",
    description: "Add an extra month of MICRO-INFUSE® serum at 67% off. Clinical studies show best results with uninterrupted treatment.",
    whyItMakesSense: [
      "Clinical results improve significantly with longer treatment",
      "Exclusive bundle pricing — today only",
      "Never miss a day of your hair regrowth routine",
      "Most customers who see results commit to 3+ months"
    ],
    price: upsellPillowPricing.price,
    originalPrice: upsellPillowPricing.originalPrice,
    savings: upsellPillowPricing.savings,
    savingsPercent: upsellPillowPricing.savingsPercent,
    image: "https://cdn.shopify.com/s/files/1/0931/6368/4113/files/6_c10c69de-cb96-4db0-b38c-a8f2b57f621b.png?v=1759597676",
    badge: "LIMITED OFFER",
    limitedQty: 12
  };
  
  const secondUpsell: SmartUpsell = {
    id: "silk-eyemask",
    name: "Scalp Massager Tool",
    headline: "Boost Absorption by 3x",
    description: "Medical-grade silicone scalp massager designed to enhance serum penetration. Stimulates blood flow to follicles for faster, more visible results.",
    whyItMakesSense: [
      "Increases serum absorption by up to 300%",
      "Stimulates blood circulation to dormant follicles",
      "Medical-grade silicone — gentle on sensitive scalps",
      "Pairs perfectly with your Colagea treatment"
    ],
    price: upsellEyemaskPricing.price,
    originalPrice: upsellEyemaskPricing.originalPrice,
    savings: upsellEyemaskPricing.savings,
    savingsPercent: upsellEyemaskPricing.savingsPercent,
    image: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/cyexzSLqIhoiQDwM.webp",
    imageSrcSet: "https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/IhaIpvoFCLPvxNTI.webp 128w, https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/NPkbBhiIAzCpymRW.webp 192w, https://files.manuscdn.com/user_upload_by_module/session_file/310519663243023818/cyexzSLqIhoiQDwM.webp 320w",
    badge: "SCALP MASSAGER",
    limitedQty: 8
  };
  
  const thirdUpsell: SmartUpsell = {
    id: "extra-pillow",
    name: "Biotin Boost Capsules",
    headline: "Supercharge Your Results From Within",
    description: "Premium biotin + zinc + vitamin D3 capsules formulated to support hair growth from the inside. The perfect complement to your topical Colagea treatment.",
    whyItMakesSense: [
      "Targets hair growth from the inside out",
      "Biotin + Zinc + D3 — the proven internal support stack",
      "Customers using both see 40% faster results",
      "Exclusive bundle-only pricing"
    ],
    price: upsellPillowPricing.price,
    originalPrice: upsellPillowPricing.originalPrice,
    savings: upsellPillowPricing.savings,
    savingsPercent: upsellPillowPricing.savingsPercent,
    image: "https://cdn.shopify.com/s/files/1/0931/6368/4113/files/6_c10c69de-cb96-4db0-b38c-a8f2b57f621b.png?v=1759597676",
    badge: "67% OFF"
  };
  
  const smartUpsell = firstUpsell; // For backward compatibility

  // ============================================================
  // UPSELL ORDERING: Swap product data into UI slots based on quantity
  // The 3 UI slots (slot1/slot2/slot3) have their own state machinery.
  // We just change which product data each slot displays.
  // ============================================================
  const upsellCatalog: Record<UpsellProductId, SmartUpsell> = {
    'pillowcase-set': firstUpsell,
    'silk-eyemask': secondUpsell,
    'extra-pillow': thirdUpsell,
  };
  const upsellOrderConfig = getUpsellOrder(selectedQuantity);
  const [slot1Upsell, slot2Upsell, slot3Upsell] = upsellOrderConfig.order.map(id => upsellCatalog[id]);

  // GA4 product ID mapping per upsell product
  const ga4UpsellIdMap: Record<UpsellProductId, string> = {
    'pillowcase-set': GA4_PRODUCT_IDS.upsells['pillowcases'],
    'silk-eyemask': GA4_PRODUCT_IDS.upsells['silk-eyemask'],
    'extra-pillow': GA4_PRODUCT_IDS.upsells[selectedSize === 'King' ? 'king-pillow' : 'standard-pillow'],
  };
  const ga4PromotionIdMap: Record<UpsellProductId, string> = {
    'pillowcase-set': 'upsell_pillowcases',
    'silk-eyemask': 'upsell_silk_eyemask',
    'extra-pillow': 'upsell_extra_pillow',
  };
  
  // Geo-detection for shipping cost and location display
  const geo = useGeolocation();
  const { countryCode } = geo;
  const isUS = countryCode ? isUSVisitor(countryCode) : true; // Default to US if unknown
  const nonUsBadgeText = useNonUsBadgeText(); // Configurable badge for non-US visitors
  
  // Calculate total items for shipping (pillows + upsell items)
  // Map slot selections back to product-specific booleans for checkout + totals
  const isPillowcaseSelected = 
    (slot1Upsell.id === 'pillowcase-set' && selectedUpsell) ||
    (slot2Upsell.id === 'pillowcase-set' && selectedSecondUpsell) ||
    (slot3Upsell.id === 'pillowcase-set' && selectedThirdUpsell);
  const isSilkEyemaskSelected = 
    (slot1Upsell.id === 'silk-eyemask' && selectedUpsell) ||
    (slot2Upsell.id === 'silk-eyemask' && selectedSecondUpsell) ||
    (slot3Upsell.id === 'silk-eyemask' && selectedThirdUpsell);
  const isExtraPillowSelected = 
    (slot1Upsell.id === 'extra-pillow' && selectedUpsell) ||
    (slot2Upsell.id === 'extra-pillow' && selectedSecondUpsell) ||
    (slot3Upsell.id === 'extra-pillow' && selectedThirdUpsell);

  const totalItemsForShipping = selectedQuantity + (isExtraPillowSelected ? 1 : 0) + (isPillowcaseSelected ? pillowcaseQuantity * 2 : 0) + (isSilkEyemaskSelected ? 1 : 0);
  const shippingCost = countryCode ? localeShippingCost(countryCode, totalItemsForShipping) : 0;
  const insuranceCost = shippingInsurance ? localeInsuranceCost : 0;

  // Calculate total with all three upsells (using slot data)
  // Helper: get price for a slot, applying pillowcase quantity multiplier if applicable
  const getSlotPrice = (upsell: SmartUpsell, selected: boolean) => {
    if (!selected) return { price: 0, originalPrice: 0, savings: 0 };
    return { price: upsell.price, originalPrice: upsell.originalPrice, savings: upsell.savings };
  };
  const slot1Prices = getSlotPrice(slot1Upsell, selectedUpsell);
  const slot2Prices = getSlotPrice(slot2Upsell, selectedSecondUpsell);
  const slot3Prices = getSlotPrice(slot3Upsell, selectedThirdUpsell);

  // Display prices: always show quantity-aware price (even before Add is clicked)
  // These are for UI display only — totals still use slot*Prices which are 0 when not selected
  const getDisplayPrice = (upsell: SmartUpsell) => {
    return { price: upsell.price, originalPrice: upsell.originalPrice, savings: upsell.savings };
  };
  const slot1Display = getDisplayPrice(slot1Upsell);
  const slot2Display = getDisplayPrice(slot2Upsell);
  const slot3Display = getDisplayPrice(slot3Upsell);
  const firstUpsellPrice = slot1Prices.price;
  const secondUpsellPrice = slot2Prices.price;
  const thirdUpsellPrice = slot3Prices.price;
  const subtotalPrice = selectedPricing.price + firstUpsellPrice + secondUpsellPrice + thirdUpsellPrice;
  const totalPrice = subtotalPrice + shippingCost + insuranceCost;
  const totalOriginalPrice = selectedPricing.originalPrice + slot1Prices.originalPrice + slot2Prices.originalPrice + slot3Prices.originalPrice;
  const totalSavings = selectedPricing.savings + slot1Prices.savings + slot2Prices.savings + slot3Prices.savings;

  // Max reachable total: base price + all upsells at max + insurance
  // Pillowcases max qty is 4, eyemask is 1, extra pillow is 1
  const pillowcaseUpsellData = upsellCatalog['pillowcase-set'];
  const eyemaskUpsellData = upsellCatalog['silk-eyemask'];
  const extraPillowUpsellData = upsellCatalog['extra-pillow'];
  // Gift qualification is based on product subtotal only (best practice).
  // Shipping fees and insurance are service charges, not product spend,
  // so they should NOT count toward gift tier thresholds.
  const maxReachableTotal = selectedPricing.price + (pillowcaseUpsellData.price * 4) + eyemaskUpsellData.price + extraPillowUpsellData.price;
  const giftCartTotal = subtotalPrice;

  // Build locale-aware gift tiers for use in checkout logic and gift display
  const allGiftTiers = useMemo(() => buildGiftTiers(giftTiers, fp), [giftTiers, fp]);
  
  // ─── ATC tracking removed from OrderPopup ──────────────────────────────
  // ATC is tracked at two points only:
  // 1. Landing page CTA click (Home.tsx) — captures purchase intent
  // 2. Shopify checkout page load — Shopify's own pixel fires ATC
  // This avoids duplicate/inflated ATC counts from popup cart mutations.

  // GA4 Tracking Helper Functions — dynamic based on slot upsell data
  const getSlotTrackingPrice = (upsell: SmartUpsell) => {
    return { price: upsell.price, originalPrice: upsell.originalPrice };
  };

  const trackUpsell1Add = useCallback(() => {
    const tp = getSlotTrackingPrice(slot1Upsell);
    const item = createUpsellItem(ga4UpsellIdMap[slot1Upsell.id as UpsellProductId], slot1Upsell.name, tp.price, tp.originalPrice);
    const promo = createUpsellPromotion(ga4PromotionIdMap[slot1Upsell.id as UpsellProductId], slot1Upsell.headline, item, 'upsell_1');
    trackSelectPromotion(promo);
    // ATC removed — tracked by landing page CTA + Shopify checkout only
  }, [slot1Upsell, pillowcaseQuantity]);
  
  const trackUpsell1Remove = useCallback(() => {
    const tp = getSlotTrackingPrice(slot1Upsell);
    const item = createUpsellItem(ga4UpsellIdMap[slot1Upsell.id as UpsellProductId], slot1Upsell.name, tp.price, tp.originalPrice);
    trackRemoveFromCart([item], tp.price);
  }, [slot1Upsell, pillowcaseQuantity]);
  
  const trackUpsell2Add = useCallback(() => {
    const tp = getSlotTrackingPrice(slot2Upsell);
    const item = createUpsellItem(ga4UpsellIdMap[slot2Upsell.id as UpsellProductId], slot2Upsell.name, tp.price, tp.originalPrice);
    const promo = createUpsellPromotion(ga4PromotionIdMap[slot2Upsell.id as UpsellProductId], slot2Upsell.headline, item, 'upsell_2');
    trackSelectPromotion(promo);
    // ATC removed — tracked by landing page CTA + Shopify checkout only
  }, [slot2Upsell, pillowcaseQuantity]);
  
  const trackUpsell2Remove = useCallback(() => {
    const tp = getSlotTrackingPrice(slot2Upsell);
    const item = createUpsellItem(ga4UpsellIdMap[slot2Upsell.id as UpsellProductId], slot2Upsell.name, tp.price, tp.originalPrice);
    trackRemoveFromCart([item], tp.price);
  }, [slot2Upsell, pillowcaseQuantity]);
  
  const trackUpsell3Add = useCallback(() => {
    const tp = getSlotTrackingPrice(slot3Upsell);
    const item = createUpsellItem(ga4UpsellIdMap[slot3Upsell.id as UpsellProductId], slot3Upsell.name, tp.price, tp.originalPrice);
    const promo = createUpsellPromotion(ga4PromotionIdMap[slot3Upsell.id as UpsellProductId], slot3Upsell.headline, item, 'upsell_3');
    trackSelectPromotion(promo);
    // ATC removed — tracked by landing page CTA + Shopify checkout only
  }, [slot3Upsell, pillowcaseQuantity]);
  
  const trackUpsell3Remove = useCallback(() => {
    const tp = getSlotTrackingPrice(slot3Upsell);
    const item = createUpsellItem(ga4UpsellIdMap[slot3Upsell.id as UpsellProductId], slot3Upsell.name, tp.price, tp.originalPrice);
    trackRemoveFromCart([item], tp.price);
  }, [slot3Upsell, pillowcaseQuantity]);

  // 15-minute countdown timer
  useEffect(() => {
    if (isOpen && step === 2 && dealSearchComplete) {
      timerRef.current = setInterval(() => {
        setDealTimer(prev => {
          if (prev.seconds === 0) {
            if (prev.minutes === 0) {
              if (timerRef.current) clearInterval(timerRef.current);
              return prev;
            }
            return { minutes: prev.minutes - 1, seconds: 59 };
          }
          return { ...prev, seconds: prev.seconds - 1 };
        });
      }, 1000);

      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }
  }, [isOpen, step, dealSearchComplete]);

  // Locale-to-location mapping is now handled by useGeolocation() which merges
  // real IP data with locale overrides. We still need this for warehouse setup fallback.
  const LOCALE_LOCATION_MAP: Record<string, UserLocation> = {
    'GB': { city: 'London', country: 'United Kingdom', countryCode: 'GB', state: '' },
    'AU': { city: 'Sydney', country: 'Australia', countryCode: 'AU', state: 'NSW' },
    'EU': { city: null, country: 'Europe', countryCode: 'EU', state: null },
    'CA': { city: 'Toronto', country: 'Canada', countryCode: 'CA', state: 'ON' },
    'NZ': { city: 'Auckland', country: 'New Zealand', countryCode: 'NZ', state: '' },
  };

  // Locale-to-warehouse mapping for forced override on locale routes
  const LOCALE_WAREHOUSE_MAP: Record<string, { name: string; location: string }> = {
    'GB': { name: 'UK Fulfillment', location: 'London, UK' },
    'AU': { name: 'Australia Center', location: 'Sydney, AU' },
    'EU': { name: 'EU Distribution', location: 'Amsterdam, NL' },
    'CA': { name: 'Canada Distribution', location: 'Toronto, ON' },
    'NZ': { name: 'Pacific Hub', location: 'Auckland, NZ' },
  };

  // Detect user location and set up warehouses
  useEffect(() => {
    if (isOpen && !warehouseInitializedRef.current) {
      warehouseInitializedRef.current = true; // Only initialize once per popup open
      // LOCALE OVERRIDE: When on a locale route (e.g., /uk-en), force location to match
      if (isInternational && locale.countryCode && LOCALE_LOCATION_MAP[locale.countryCode]) {
        setUserLocation(LOCALE_LOCATION_MAP[locale.countryCode]);
        const localeWarehouse = LOCALE_WAREHOUSE_MAP[locale.countryCode];
        if (localeWarehouse) {
          setWarehouses([
            ...baseWarehouses,
            { id: 'local', name: localeWarehouse.name, location: localeWarehouse.location }
          ]);
        } else {
          setWarehouses([...baseWarehouses]);
        }
      } else {
        try {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          
          if (timezoneMap[timezone]) {
            setUserLocation(timezoneMap[timezone]);
          }

          const localWarehouse = locationWarehouseMap[timezone];
          if (localWarehouse) {
            setWarehouses([
              ...baseWarehouses,
              { id: "local", name: localWarehouse.name, location: localWarehouse.location }
            ]);
          } else {
            setWarehouses([
              ...baseWarehouses,
              { id: "ga", name: "Georgia Center", location: "Atlanta, GA" }
            ]);
          }
        } catch {
          setWarehouses([
            ...baseWarehouses,
            { id: "ga", name: "Georgia Center", location: "Atlanta, GA" }
          ]);
        }
      }
    }
  }, [isOpen, isInternational, locale.countryCode]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      scrollPositionRef.current = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollPositionRef.current);
    }
    
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [isOpen]);

  // Run warehouse animation when popup opens (Step 1)
  // Skip animation on reopen and restore previous selections
  useEffect(() => {
    if (isOpen && step === 1 && warehouses.length > 0) {
      // If user has opened checkout before, skip animation and go directly to step 2
      if (hasOpenedBeforeRef.current) {
        // Check if the main variant (quantity) changed since last close
        const variantChanged = lastClosedQuantityRef.current !== selectedQuantity;
        
        if (variantChanged) {
          // Reset all upsell selections when variant changes
          setSelectedUpsell(false);
          setSelectedSecondUpsell(false);
          setSelectedThirdUpsell(false);
          setFirstUpsellCollapsed(false);
          setSecondUpsellCollapsed(false);
          setThirdUpsellCollapsed(false);
          setShowSecondUpsell(false);
          setShowThirdUpsell(false);
          // Reset pillowcase quantity based on upsell order config
          const newPcQty = upsellOrderConfig.defaultPillowcaseQuantity;
          setPillowcaseQuantity(newPcQty);
          // Clear persisted refs
          persistedUpsellRef.current = false;
          persistedSecondUpsellRef.current = false;
          persistedThirdUpsellRef.current = false;
          persistedFirstUpsellCollapsedRef.current = false;
          persistedSecondUpsellCollapsedRef.current = false;
          persistedThirdUpsellCollapsedRef.current = false;
          persistedShowSecondUpsellRef.current = false;
          persistedShowThirdUpsellRef.current = false;
          persistedPillowcaseQuantityRef.current = newPcQty;
          // Keep shipping insurance persisted
          setShippingInsurance(persistedShippingInsuranceRef.current);
        } else {
          // Restore persisted selections (same variant)
          setSelectedUpsell(persistedUpsellRef.current);
          setSelectedSecondUpsell(persistedSecondUpsellRef.current);
          setSelectedThirdUpsell(persistedThirdUpsellRef.current);
          setShippingInsurance(persistedShippingInsuranceRef.current);
          // Restore collapsed states from persisted refs (not derived from selection)
          setFirstUpsellCollapsed(persistedFirstUpsellCollapsedRef.current);
          setSecondUpsellCollapsed(persistedSecondUpsellCollapsedRef.current);
          setThirdUpsellCollapsed(persistedThirdUpsellCollapsedRef.current);
          setShowSecondUpsell(persistedShowSecondUpsellRef.current);
          setShowThirdUpsell(persistedShowThirdUpsellRef.current);
          setPillowcaseQuantity(persistedPillowcaseQuantityRef.current);
        }
        // Skip to step 2 immediately
        setStep(2);
        setDealSearchActive(false);
        setDealSearchComplete(true);
        return;
      }
      
      // First time opening - run the animation
      // Set pillowcase quantity based on upsell order config
      const defaultPcQty = upsellOrderConfig.defaultPillowcaseQuantity;
      setPillowcaseQuantity(defaultPcQty);
      persistedPillowcaseQuantityRef.current = defaultPcQty;
      
      // Clear any previous timeouts
      animationTimeoutsRef.current.forEach(t => clearTimeout(t));
      animationTimeoutsRef.current = [];
      
      setWarehouseStatuses({});
      setFoundWarehouse(null);
      setIsTransitioning(false);

      const getDelay = (index: number): number => {
        if (index === 0) return 400;
        if (index === 1) return 1600;
        return 3200;
      };

      const getCheckDuration = (index: number): number => {
        if (index === 0) return 600;
        if (index === 1) return 900;
        return 1200;
      };

      const availableIndex = warehouses.length - 1;
      
      warehouses.forEach((warehouse, index) => {
        const delay = getDelay(index);
        const checkDuration = getCheckDuration(index);

        const t1 = setTimeout(() => {
          setWarehouseStatuses(prev => ({
            ...prev,
            [warehouse.id]: "checking"
          }));
        }, delay);
        animationTimeoutsRef.current.push(t1);

        const t2 = setTimeout(() => {
          const status = index === availableIndex ? "available" : "unavailable";
          setWarehouseStatuses(prev => ({
            ...prev,
            [warehouse.id]: status
          }));
          
          if (status === "available") {
            setFoundWarehouse(warehouse.id);
          }
        }, delay + checkDuration);
        animationTimeoutsRef.current.push(t2);
      });

      // Transition to step 2
      const lastDelay = getDelay(warehouses.length - 1);
      const lastCheckDuration = getCheckDuration(warehouses.length - 1);
      const totalDuration = lastDelay + lastCheckDuration + 800;
      
      const t3 = setTimeout(() => {
        setIsTransitioning(true);
        const t4 = setTimeout(() => {
          setStep(2);
          setIsTransitioning(false);
          // Reset deal search state for step 2
          setDealSearchActive(true);
          setDealSearchIndex(0);
          setDealSearchComplete(false);
          
          // GA4: Track begin_checkout when reaching step 2
          const qty = selectedQuantity as 1 | 2 | 4;
          const ga4Item = createMainProductItem(
            selectedSize, 
            qty, 
            selectedPricing.price, 
            selectedPricing.originalPrice
          );
          trackBeginCheckout([ga4Item], selectedPricing.price);
        }, 300);
        animationTimeoutsRef.current.push(t4);
      }, totalDuration);
      animationTimeoutsRef.current.push(t3);
    }
    
    return () => {
      // Clean up all animation timeouts on unmount or re-run
      animationTimeoutsRef.current.forEach(t => clearTimeout(t));
      animationTimeoutsRef.current = [];
    };
  }, [isOpen, step, warehouses]);

  // Run inline deal search animation when step 2 loads
  useEffect(() => {
    if (isOpen && step === 2 && dealSearchActive && !dealSearchComplete) {
      // Slower intervals toward the end for dramatic effect
      const messageIntervals = [600, 900, 1200, 1500];
      let totalDelay = 0;

      messageIntervals.forEach((interval, i) => {
        totalDelay += interval;
        setTimeout(() => {
          setDealSearchIndex(i + 1);
          if (i === messageIntervals.length - 1) {
            setTimeout(() => {
              setDealSearchComplete(true);
              setDealSearchActive(false);
              
              // GA4: Track view_promotion when upsells are revealed (slot-based)
              const tp1 = getSlotTrackingPrice(slot1Upsell);
              const tp2 = getSlotTrackingPrice(slot2Upsell);
              const tp3 = getSlotTrackingPrice(slot3Upsell);
              const upsell1Item = createUpsellItem(ga4UpsellIdMap[slot1Upsell.id as UpsellProductId], slot1Upsell.name, tp1.price, tp1.originalPrice);
              const upsell2Item = createUpsellItem(ga4UpsellIdMap[slot2Upsell.id as UpsellProductId], slot2Upsell.name, tp2.price, tp2.originalPrice);
              const upsell3Item = createUpsellItem(ga4UpsellIdMap[slot3Upsell.id as UpsellProductId], slot3Upsell.name, tp3.price, tp3.originalPrice);
              
              const promotions = [
                createUpsellPromotion(ga4PromotionIdMap[slot1Upsell.id as UpsellProductId], slot1Upsell.headline, upsell1Item, 'upsell_1'),
                createUpsellPromotion(ga4PromotionIdMap[slot2Upsell.id as UpsellProductId], slot2Upsell.headline, upsell2Item, 'upsell_2'),
                createUpsellPromotion(ga4PromotionIdMap[slot3Upsell.id as UpsellProductId], slot3Upsell.headline, upsell3Item, 'upsell_3')
              ];
              
              trackViewPromotion(promotions);
              
              // Second/third upsells removed — single upsell flow only
            }, 400);
          }
        }, totalDelay);
      });
    }
  }, [isOpen, step, dealSearchActive, dealSearchComplete, hideFirstUpsell, hideSecondUpsell]);

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Viewing counter animation (matching product form)
  useEffect(() => {
    if (!isOpen || step !== 2) return;
    
    const updateViewerCount = () => {
      const change = Math.random() > 0.5 ? 1 : -1;
      const newCount = Math.max(35, Math.min(65, viewerCount + change));
      setViewerCount(newCount);
    };
    
    const interval = setInterval(updateViewerCount, 3000 + Math.random() * 2000);
    return () => clearInterval(interval);
  }, [isOpen, step, viewerCount]);

  // Animate viewer count changes
  useEffect(() => {
    if (viewerCount !== displayedViewerCount) {
      setIsViewerCountAnimating(true);
      const timeout = setTimeout(() => {
        setDisplayedViewerCount(viewerCount);
        setIsViewerCountAnimating(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [viewerCount, displayedViewerCount]);

  // Purchased today counter (matching product form logic)
  useEffect(() => {
    if (!isOpen || step !== 2) return;
    
    const calculatePurchasedToday = () => {
      const now = new Date();
      const nycTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const hours = nycTime.getHours();
      const minutes = nycTime.getMinutes();
      
      const minPurchases = 23;
      const maxPurchases = 89;
      const totalGrowth = maxPurchases - minPurchases;
      
      // Non-linear growth curve
      const totalMinutes = hours * 60 + minutes;
      const maxMinutes = 24 * 60;
      let progress = totalMinutes / maxMinutes;
      
      // Peak hours adjustment
      if (hours >= 9 && hours <= 11) progress *= 1.3;
      else if (hours >= 19 && hours <= 22) progress *= 1.2;
      
      progress = Math.min(1, progress);
      return Math.floor(minPurchases + (totalGrowth * progress));
    };
    
    setPurchasedToday(calculatePurchasedToday());
    const interval = setInterval(() => setPurchasedToday(calculatePurchasedToday()), 120000);
    return () => clearInterval(interval);
  }, [isOpen, step]);

  // Animate purchased today changes
  useEffect(() => {
    if (purchasedToday !== displayedPurchasedToday) {
      setIsPurchasedAnimating(true);
      const timeout = setTimeout(() => {
        setDisplayedPurchasedToday(purchasedToday);
        setIsPurchasedAnimating(false);
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [purchasedToday, displayedPurchasedToday]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTab = (e: KeyboardEvent) => {
        if (e.key === "Tab") {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      window.addEventListener("keydown", handleTab);
      firstElement?.focus();
      
      return () => window.removeEventListener("keydown", handleTab);
    }
  }, [isOpen, step]);

  const handleClose = useCallback(() => {
    // Persist current selections before closing
    persistedUpsellRef.current = selectedUpsell;
    persistedSecondUpsellRef.current = selectedSecondUpsell;
    persistedThirdUpsellRef.current = selectedThirdUpsell;
    persistedShippingInsuranceRef.current = shippingInsurance;
    persistedFirstUpsellCollapsedRef.current = firstUpsellCollapsed;
    persistedSecondUpsellCollapsedRef.current = secondUpsellCollapsed;
    persistedThirdUpsellCollapsedRef.current = thirdUpsellCollapsed;
    persistedShowSecondUpsellRef.current = showSecondUpsell;
    persistedShowThirdUpsellRef.current = showThirdUpsell;
    persistedPillowcaseQuantityRef.current = pillowcaseQuantity;
    
    // Track the quantity at close time for detecting variant changes
    lastClosedQuantityRef.current = selectedQuantity;
    // Mark that checkout has been opened before (skip animation on next open)
    hasOpenedBeforeRef.current = true;
    // Reset warehouse initialized flag so it can re-initialize on next open
    warehouseInitializedRef.current = false;
    // Clear any pending animation timeouts
    animationTimeoutsRef.current.forEach(t => clearTimeout(t));
    animationTimeoutsRef.current = [];
    
    // Reset step but keep persisted state in refs
    setStep(1);
    // Don't reset upsell state - it will be restored from refs on reopen
    setExpandedDetails(false);
    setExpandedAccordion(null);
    // Keep timer running in background (don't reset)
    // setDealTimer({ minutes: 15, seconds: 0 });
    setDealSearchActive(true);
    setDealSearchIndex(0);
    setDealSearchComplete(false);
    if (timerRef.current) clearInterval(timerRef.current);
    // Trigger exit recovery modal if user closes without completing checkout
    if (onExitRecovery && step === 2) {
      onExitRecovery();
    }
    onClose();
  }, [onClose, onExitRecovery, step, selectedUpsell, selectedSecondUpsell, selectedThirdUpsell, shippingInsurance, firstUpsellCollapsed, secondUpsellCollapsed, thirdUpsellCollapsed, showSecondUpsell, showThirdUpsell, pillowcaseQuantity]);

  const handleProceedToCheckout = () => {
    // Set loading state immediately
    setIsCheckoutLoading(true);
    
    // 3-upsell flow (Feb 2026) — slot order varies by quantity
    // Use product-specific booleans (isPillowcaseSelected etc.) for checkout URL
    
    // Determine which gifts are unlocked based on cart total
    const checkoutActiveTiers = getActiveTiers(allGiftTiers, maxReachableTotal, isUS);
    const slippersUnlocked = checkoutActiveTiers.some(t => t.id === 'slippers' && giftCartTotal >= t.threshold);
    const linenSprayUnlocked = checkoutActiveTiers.some(t => t.id === 'spray' && giftCartTotal >= t.threshold);
    
    // Generate Shopify checkout URL — map from product booleans, not slot positions
    const checkoutUrl = generateCheckoutUrl({
      size: selectedSize,
      quantity: selectedQuantity as 1 | 2 | 4,
      secondUpsell: isPillowcaseSelected, // Pillowcase set
      pillowcaseQuantity: isPillowcaseSelected ? pillowcaseQuantity : undefined,
      silkEyemask: isSilkEyemaskSelected, // Silk eyemask
      thirdUpsell: isExtraPillowSelected, // Extra pillow
      shippingInsurance: shippingInsurance,
      pageSlug: pageSlug,
      giftSlippers: slippersUnlocked,
      slipperSize: slipperSize,
      giftLinenSpray: linenSprayUnlocked,
      isUS: isUS,
      subtotal: subtotalPrice,
    });
    
    // Log for debugging
    console.log('Checkout URL:', checkoutUrl);
    console.log('Cart:', {
      size: selectedSize,
      quantity: selectedQuantity,
      upsellOrder: upsellOrderConfig.order,
      pillowcaseSet: isPillowcaseSelected,
      silkEyemask: isSilkEyemaskSelected,
      extraPillow: isExtraPillowSelected,
      total: totalPrice.toFixed(2),
      savings: totalSavings.toFixed(2),
    });
    
    // Small delay to show loading state before redirect
    setTimeout(() => {
      window.location.href = checkoutUrl;
    }, 100);
  };

  if (!isOpen) return null;

  const getShippingText = () => {
    // Use correct delivery times: US = 2-3 days, International = 5-8 days
    const deliveryDays = isUS ? "2-3" : "5-8";
    // Use unified formatLocationText for consistent display:
    // US: "City, State" (e.g., "Austin, TX")
    // International: "City, Country" (e.g., "Glasgow, United Kingdom")
    const locationDisplay = formatLocationText(geo);
    if (locationDisplay) {
      // Get the flag from the real IP countryCode for accuracy
      const flagCode = geo.countryCode || userLocation.countryCode || '';
      const flag = countryFlags[flagCode] || "";
      return (
        <span className="flex items-center gap-1.5">
          {flag && <span>{flag}</span>}
          Ships to {locationDisplay}, delivers in {deliveryDays} days
        </span>
      );
    }
    return `Ready to ship, delivers in ${deliveryDays} days`;
  };

  // What's included items
  const whatsIncluded = [
    `${selectedQuantity}x Colagea Hair Regrowth Serum (${selectedQuantity * 3} vials)`,
    "Free Welcome Pack included",
    "Micro-needle applicator cap",
    "Treatment guide & usage calendar",
    "90-day money-back guarantee"
  ];

  return (
    <div 
      className="fixed inset-0 z-[9999] backdrop-blur-sm flex items-center justify-center bg-background/95"
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-popup-title"
    >
      <div 
        ref={modalRef}
        className={`w-full h-full overflow-y-auto transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
      >

        
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="fixed top-4 right-4 z-[10000] w-10 h-10 rounded-full flex items-center justify-center transition-colors bg-secondary hover:bg-secondary/80"
          aria-label="Close popup"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Step 1: Warehouse Stock Verification */}
        {step === 1 && (
          <div className="min-h-full flex flex-col items-center justify-center px-4 py-12">
            <div className="max-w-lg w-full text-center">
              <h2 id="order-popup-title" className="text-2xl lg:text-3xl font-semibold mb-2 font-heading uppercase">
                Verifying Stock Levels
              </h2>
              <p className="text-muted-foreground mb-8">
                Checking availability across our distribution network...
              </p>

              {/* Warehouse Cards */}
              <div className="space-y-3">
                {warehouses.map((warehouse) => {
                  const status = warehouseStatuses[warehouse.id];
                  const isLocal = warehouse.id === "local";
                  return (
                    <div
                      key={warehouse.id}
                      className={`bg-card border rounded-xl p-4 flex items-center justify-between transition-all duration-300 ${
                        status === "available" 
                          ? "border-green-500 bg-green-50 dark:bg-green-950/30" 
                          : status === "unavailable"
                          ? "border-border opacity-60"
                          : status === "checking"
                          ? "border-amber-400"
                          : "border-border"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          status === "available" 
                            ? "bg-green-500" 
                            : status === "unavailable"
                            ? "bg-secondary"
                            : status === "checking"
                            ? "bg-amber-400"
                            : "bg-secondary"
                        }`}>
                          {status === "checking" ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : status === "available" ? (
                            <Check className="w-5 h-5 text-white" />
                          ) : status === "unavailable" ? (
                            <X className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <Package className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                        <div className="text-left">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{warehouse.name}</span>
                            {isLocal && status === "available" && (
                              <span className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full">
                                Near You
                              </span>
                            )}
                          </div>
                          <span className="text-sm text-muted-foreground">{warehouse.location}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        {status === "checking" && (
                          <span className="text-sm text-amber-600">Checking...</span>
                        )}
                        {status === "available" && (
                          <span className="text-sm text-green-600 font-medium">In Stock!</span>
                        )}
                        {status === "unavailable" && (
                          <span className="text-sm text-muted-foreground">Out of Stock</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Stock found message */}
              {foundWarehouse && (
                <div className="mt-6 p-4 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-xl animate-in fade-in duration-500">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 text-green-700 dark:text-green-400 text-center">
                    <div className="flex items-center gap-2">
                      <Check className="w-5 h-5 flex-shrink-0" />
                      <span className="font-medium">Stock confirmed!</span>
                    </div>
                    <span className="font-medium sm:font-normal">Preparing your order...</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Order Summary with Inline Deal Search in Upsell Section */}
        {step === 2 && (
          <div className="min-h-full flex flex-col items-center px-4 py-8 lg:py-12">
            <div className="max-w-2xl w-full">
              {/* Header */}
              <div className="text-center mb-6">
                <h2 className="text-2xl lg:text-3xl font-semibold mb-2 font-heading uppercase">
                  Complete Your Order
                </h2>
                <p className="text-muted-foreground">
                  Review your selection and proceed to checkout
                </p>
              </div>

              {/* Order Summary Card */}
              <div className="bg-card border rounded-2xl p-4 sm:p-5 mb-4">
                <div className="flex items-start gap-3 sm:gap-4 mb-4">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 rounded overflow-hidden bg-[#e8f4f8] flex-shrink-0">
                    <img 
                      src="https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/colagea-box-product_08474614.jpg"
                      alt="Colagea Serum"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-sm sm:text-base whitespace-nowrap">Colagea Hair Regrowth Serum</h3>
                      <span className="flex items-center gap-1.5">
                        <span className="font-semibold text-sm sm:text-base">× {selectedQuantity}</span>
                        {selectedQuantity === 2 && (
                          <span className="text-[10px] bg-[#004C99] text-white px-2 py-0.5 rounded-full font-medium whitespace-nowrap">Most Popular</span>
                        )}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                      <span className="text-base sm:text-lg font-bold">{fp(selectedPricing.price)}</span>
                      <span className="text-xs sm:text-sm text-muted-foreground line-through">{fp(selectedPricing.originalPrice)}</span>
                    </div>
                    <div className="text-xs text-green-600 font-semibold mb-2">
                      Instant saving: {fp(selectedPricing.savings)}
                    </div>
                    <div className="flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground">
                      <Truck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      {getShippingText()}
                    </div>
                  </div>
                </div>

                {/* What's Included Accordion */}
                <button
                  onClick={() => setExpandedAccordion(expandedAccordion === "included" ? null : "included")}
                  className="w-full flex items-center justify-between pt-3 pb-1 border-t border-border text-sm"
                >
                  <span className="font-medium">What's Included</span>
                  {expandedAccordion === "included" ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>
                {expandedAccordion === "included" && (
                  <div className="pt-1.5 pb-2 space-y-2">
                    {whatsIncluded.map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

               {/* Gift Progress Bar removed per user request */}

              {/* Upsell Section - With Inline Deal Search Animation */}
              <div className={`relative border-2 rounded-2xl overflow-hidden transition-all duration-500 mb-4 ${
                dealSearchComplete && selectedUpsell 
                  ? "bg-white dark:bg-[#0a1628]/30 border-green-500" 
                  : "bg-gradient-to-br from-[#f0f7ff] to-[#e8f4f8] dark:from-[#0a1628]/50 dark:to-[#003d7a]/50 border-[#003d7a]/30"
              }`}>
                
                {/* Inline Deal Search Animation - Shows while searching */}
                {!dealSearchComplete && (
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0066CC] to-[#003d7a] flex items-center justify-center">
                        <Search className="w-5 h-5 text-[#f5c542] animate-pulse" />
                      </div>
                      <div>
                        <h3 className="font-semibold">Searching for Additional Deals</h3>
                        <p className="text-sm text-muted-foreground">Finding the best offer for your order...</p>
                      </div>
                    </div>

                    {/* Search progress */}
                    <div className="space-y-2">
                      {dealSearchMessages.map((message, index) => {
                        const Icon = message.icon;
                        const isActive = index === dealSearchIndex;
                        const isComplete = index < dealSearchIndex;
                        
                        return (
                          <div
                            key={index}
                            className={`flex items-center gap-2 p-2 rounded-lg transition-all duration-300 ${
                              isActive 
                                ? "bg-[#0066CC]/10" 
                                : isComplete
                                ? "bg-green-100 dark:bg-green-900/30"
                                : "opacity-40"
                            }`}
                          >
                            <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                              isActive 
                                ? "bg-[#0066CC]" 
                                : isComplete
                                ? "bg-green-500"
                                : "bg-secondary"
                            }`}>
                              {isComplete ? (
                                <Check className="w-3 h-3 text-white" />
                              ) : isActive ? (
                                <div className="w-3 h-3 border-2 border-[#f5c542] border-t-transparent rounded-full animate-spin" />
                              ) : (
                                <Icon className="w-3 h-3 text-muted-foreground" />
                              )}
                            </div>
                            <span className={`text-sm ${isActive ? "font-medium" : ""}`}>
                              {message.text}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Revealed Upsell Card - Shows after search completes */}
                {dealSearchComplete && !hideFirstUpsell && (
                  <>
                    {/* Badge with integrated timer - always visible */}
                    {slot1Upsell.badge && (
                      <div className={`absolute top-0 left-0 right-0 z-10 text-white text-[10px] sm:text-xs font-bold py-1.5 sm:py-2 px-3 sm:px-4 flex items-center justify-between transition-all duration-400 ease-out ${
                        selectedUpsell 
                          ? "bg-gradient-to-r from-green-600 to-emerald-600" 
                          : "bg-gradient-to-r from-[#0066CC] to-[#003d7a]"
                      }`}>
                        <span className="truncate">
                          {selectedUpsell ? 'ADDED TO CART' : `${slot1Upsell.badge} - ${slot1Upsell.savingsPercent}% OFF`}
                        </span>
                        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
                          <span className="hidden sm:inline text-white/80 font-normal">OFFER RESERVED FOR</span>
                          <div className="flex items-center gap-0.5 sm:gap-1 font-mono">
                            <span className="bg-white/20 px-1 sm:px-1.5 py-0.5 rounded text-[10px] sm:text-xs">{String(dealTimer.minutes).padStart(2, '0')}</span>
                            <span className="text-[#f5c542]">:</span>
                            <span className="bg-white/20 px-1 sm:px-1.5 py-0.5 rounded text-[10px] sm:text-xs">{String(dealTimer.seconds).padStart(2, '0')}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Single container with smooth resize animation - like Black Friday globe section */}
                    <div 
                      className="overflow-hidden"
                      style={{
                        maxHeight: firstUpsellCollapsed ? '120px' : '800px',
                        background: 'transparent',
                        transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), background 0.5s ease-out',
                      }}
                    >
                      {/* Content that smoothly resizes - same elements, different sizes based on collapsed state */}
                      <div className={`${
                        firstUpsellCollapsed 
                          ? 'p-3 sm:p-4 pt-10 sm:pt-12' 
                          : 'p-4 sm:p-5 pt-12 sm:pt-14'
                      }`}
                      style={{
                        transition: 'padding 0.3s ease-out'
                      }}>
                        <div className={`flex items-start ${
                          firstUpsellCollapsed 
                            ? 'flex-row gap-3 sm:gap-4' 
                            : 'flex-col sm:flex-row gap-4 sm:gap-5'
                        }`}
                        style={{
                          transition: 'gap 0.3s ease-out'
                        }}>
                          {/* Product Image - resizes based on collapsed state */}
                          <div className={`flex-shrink-0 ${
                            firstUpsellCollapsed 
                              ? 'w-14 h-14 sm:w-16 sm:h-16' 
                              : 'flex gap-4 sm:block'
                          }`}>
                            <div className={`overflow-hidden ${
                              firstUpsellCollapsed 
                                ? 'w-14 h-14 sm:w-16 sm:h-16 bg-[#e8f4f8] rounded' 
                                : 'w-24 h-24 sm:w-40 sm:h-40 bg-[#e8f4f8] rounded-lg'
                            }`}>
                              <video 
                                src="https://colagea.com/cdn/shop/videos/c/vp/64338fde025747278cec0886b830deb1/64338fde025747278cec0886b830deb1.HD-1080p-2.5Mbps-57326617.mp4?v=0"
                                autoPlay
                                muted
                                loop
                                playsInline
                                className={`w-full h-full ${
                                  firstUpsellCollapsed ? 'object-contain' : 'object-cover'
                                }`}
                              />
                            </div>
                            {/* Mobile: show details next to image - hidden when collapsed */}
                            {!firstUpsellCollapsed && (
                              <div className="sm:hidden flex-1">
                                <div className="inline-flex items-center gap-1.5 bg-[#FF8080]/15 dark:bg-[#FF8080]/20 text-[#FF8080] px-2 py-0.5 rounded-full text-[10px] font-medium mb-1">
                                  <Gift className="w-2.5 h-2.5" />
                                  Exclusive Deal Found!
                                </div>
                                <h3 className="text-base font-bold leading-tight">{slot1Upsell.headline}</h3>
                                <p className="text-sm font-semibold text-foreground/80">{slot1Upsell.name}</p>
                              </div>
                            )}
                            {!firstUpsellCollapsed && slot1Upsell.limitedQty && (
                              <div className="hidden sm:block mt-2 text-center text-xs text-orange-600 dark:text-orange-400 font-medium">
                                Only {slot1Upsell.limitedQty} left at this price
                              </div>
                            )}
                          </div>

                          {/* Product Details - adapts based on collapsed state */}
                          <div className="flex-1 min-w-0">
                            {/* Collapsed state: compact info with improved text contrast */}
                            {firstUpsellCollapsed ? (
                              <>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-bold text-sm sm:text-base text-foreground truncate">{slot1Upsell.badge} - {slot1Upsell.name}</span>
                                  <span className="flex-shrink-0 inline-flex items-center gap-1 bg-green-500 text-white text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium">
                                    <Check className="w-3 h-3" />
                                    Secured
                                  </span>
                                </div>
                                <p className="text-xs sm:text-sm text-muted-foreground truncate">{slot1Upsell.description.split('.')[0]}.</p>
                              </>
                            ) : (
                              <>
                                {/* Desktop header - hidden on mobile */}
                                <div className="hidden sm:block">
                                  <div className="inline-flex items-center gap-2 bg-[#FF8080]/15 dark:bg-[#FF8080]/20 text-[#FF8080] px-2 py-1 rounded-full text-xs font-medium mb-2">
                                    <Gift className="w-3 h-3" />
                                    Exclusive Deal Found!
                                  </div>
                                  <h3 className="text-xl font-bold mb-1">{slot1Upsell.headline}</h3>
                                  <p className="text-lg font-semibold text-foreground/80 mb-2">{slot1Upsell.name}</p>
                                </div>
                                <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">{slot1Upsell.description}</p>

                                {/* Why it makes sense */}
                                <button
                                  onClick={() => setExpandedDetails(!expandedDetails)}
                                  className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium text-[#0066CC] dark:text-[#f5c542] mb-3"
                                >
                                  <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                  Why this makes sense for you
                                  {expandedDetails ? <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                                </button>
                                
                                {expandedDetails && (
                                  <div className="bg-white/50 dark:bg-black/20 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4 space-y-1.5 sm:space-y-2">
                                    {slot1Upsell.whyItMakesSense.map((reason, i) => (
                                      <div key={i} className="flex items-start gap-2 text-xs sm:text-sm">
                                        <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                        <span>{reason}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}

                            {/* Price and Add Button - adapts based on collapsed state */}
                            <div className={`transition-all duration-400 ease-out ${
                              firstUpsellCollapsed 
                                ? 'hidden' 
                                : 'flex flex-col gap-3'
                            }`}>
                              {/* Price Row */}
                              <div className="flex items-center justify-between">
                                <div />
                                <div className="text-right">
                                  <div className="flex items-baseline gap-1.5 sm:gap-2">
                                    <span className="text-xl sm:text-2xl font-bold text-green-600">{fp(slot1Display.price)}</span>
                                    <span className="text-sm sm:text-lg text-muted-foreground line-through">{fp(slot1Display.originalPrice)}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 justify-end">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#22c55e]/15 text-[#22c55e]">-{slot1Upsell.savingsPercent}%</span>
                                    <span className="text-xs sm:text-sm text-green-600 font-medium">
                                      Save {fp(slot1Display.savings)}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <button
                                onClick={(e) => {
                                  // Prevent double-click during animation
                                  if (isFirstUpsellAnimating) return;
                                  
                                  if (!selectedUpsell) {
                                    // Lock button during animation
                                    setIsFirstUpsellAnimating(true);
                                    
                                    // Accept upsell - trigger collapse animation with confetti
                                    setSelectedUpsell(true);
                                    
                                    // GA4: Track select_promotion and add_to_cart
                                    trackUpsell1Add();
                                    
                                    setTimeout(() => {
                                      setFirstUpsellCollapsed(true);
                                      // No second upsell — single upsell flow only
                                      // Unlock button after animation completes
                                      setTimeout(() => setIsFirstUpsellAnimating(false), 500);
                                    }, 600);
                                  } else {
                                    // Lock button during animation
                                    setIsFirstUpsellAnimating(true);
                                    
                                    // Remove upsell - expand back
                                    setFirstUpsellCollapsed(false);
                                    setSelectedUpsell(false);
                                    
                                    // GA4: Track remove_from_cart
                                    trackUpsell1Remove();
                                    
                                    // Unlock button after animation completes
                                    setTimeout(() => setIsFirstUpsellAnimating(false), 600);
                                  }
                                }}
                                className={`w-full sm:w-auto px-6 py-3 rounded-full font-semibold transition-all text-sm sm:text-base ${
                                  selectedUpsell
                                    ? "bg-green-500 text-white"
                                    : "bg-[#007AFF] text-white hover:bg-[#d32f2f]"
                                }`}
                              >
                                {selectedUpsell ? (
                                  <span className="flex items-center justify-center gap-2">
                                    <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                                    Added!
                                  </span>
                                ) : (
                                  "Add to Order"
                                )}
                              </button>
                            </div>
                          </div>

                          {/* Price info and remove button - only visible when collapsed */}
                          {firstUpsellCollapsed && (
                            <div className="flex-shrink-0 flex items-center gap-2 sm:gap-3">
                              <div className="text-right">
                                <div className="flex items-baseline gap-1">

                                  <span className="text-sm sm:text-base font-bold text-green-600">{fp(slot1Display.price)}</span>
                                  <span className="text-[10px] sm:text-xs text-muted-foreground line-through">{fp(slot1Display.originalPrice)}</span>
                                </div>
                                <div className="text-[9px] sm:text-[10px] text-green-600 font-medium">
                                  Save {fp(slot1Display.savings)}
                                </div>
                              </div>
                              {/* Remove button */}
                              <button
                                onClick={() => {
                                  setFirstUpsellCollapsed(false);
                                  setSelectedUpsell(false);
                                  
                                  // GA4: Track remove_from_cart
                                  trackUpsell1Remove();
                                }}
                                className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
                                title="Remove from order"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Second and Third Upsells removed — single upsell flow only */}
              {false && dealSearchComplete && showSecondUpsell && !hideSecondUpsell && (
                <div 
                  className={`relative border-2 rounded-2xl overflow-hidden mb-4 transition-all duration-400 ease-out ${
                    secondUpsellRevealing 
                      ? "opacity-100 translate-y-0 max-h-[800px]" 
                      : "opacity-0 -translate-y-4 max-h-0"
                  } ${
                    selectedSecondUpsell 
                      ? "bg-white dark:bg-[#0a1628]/30 border-green-500" 
                      : "bg-gradient-to-br from-[#f0f7ff] to-[#e8f4f8] dark:from-[#0a1628]/50 dark:to-[#003d7a]/50 border-[#003d7a]/30"
                  }`}
                  style={{ transitionProperty: 'opacity, transform, max-height, background-color, border-color' }}
                >
                  {/* Badge with timer - always visible */}
                  {slot2Upsell.badge && (
                    <div className={`absolute top-0 left-0 right-0 z-10 text-white text-[10px] sm:text-xs font-bold py-1.5 sm:py-2 px-3 sm:px-4 flex items-center justify-between transition-all duration-400 ease-out ${
                      selectedSecondUpsell 
                        ? "bg-gradient-to-r from-green-600 to-emerald-600" 
                        : "bg-gradient-to-r from-[#0066CC] to-[#003d7a]"
                      }`}>
                        <span className="truncate">
                          {selectedSecondUpsell ? 'ADDED TO CART' : `${slot2Upsell.badge} - ${slot2Upsell.savingsPercent}% OFF`}
                      </span>
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
                        <span className="hidden sm:inline text-white/80 font-normal">OFFER RESERVED FOR</span>
                        <div className="flex items-center gap-0.5 sm:gap-1 font-mono">
                          <span className="bg-white/20 px-1 sm:px-1.5 py-0.5 rounded text-[10px] sm:text-xs">{String(dealTimer.minutes).padStart(2, '0')}</span>
                          <span className="text-[#f5c542]">:</span>
                          <span className="bg-white/20 px-1 sm:px-1.5 py-0.5 rounded text-[10px] sm:text-xs">{String(dealTimer.seconds).padStart(2, '0')}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Single container with smooth resize animation - like Black Friday globe section */}
                  <div 
                    className="overflow-hidden"
                    style={{
                      maxHeight: secondUpsellCollapsed ? '120px' : '800px',
                      background: 'transparent',
                      transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), background 0.5s ease-out',
                    }}
                  >
                    {/* Content that smoothly resizes - same elements, different sizes based on collapsed state */}
                    <div className={`${
                      secondUpsellCollapsed 
                        ? 'p-3 sm:p-4 pt-10 sm:pt-12' 
                        : 'p-4 sm:p-5 pt-12 sm:pt-14'
                    }`}
                    style={{
                      transition: 'padding 0.3s ease-out'
                    }}>
                      <div className={`flex items-start ${
                        secondUpsellCollapsed 
                          ? 'flex-row gap-3 sm:gap-4' 
                          : 'flex-col sm:flex-row gap-4 sm:gap-5'
                      }`}
                      style={{
                        transition: 'gap 0.3s ease-out'
                      }}>
                        {/* Product Image - resizes based on collapsed state */}
                        <div className={`flex-shrink-0 ${
                          secondUpsellCollapsed 
                            ? 'w-14 h-14 sm:w-16 sm:h-16' 
                            : 'flex gap-4 sm:block'
                        }`}>
                          <div className={`overflow-hidden ${
                            secondUpsellCollapsed 
                              ? 'w-14 h-14 sm:w-16 sm:h-16 bg-[#f5f0e6] rounded' 
                              : 'w-24 h-24 sm:w-40 sm:h-40 bg-[#f5f0e6] rounded-lg'
                          }`}>
                            <img 
                              src={slot2Upsell.image}
                              srcSet={slot2Upsell.imageSrcSet}
                              sizes={secondUpsellCollapsed ? '64px' : '(min-width: 640px) 160px, 96px'}
                              alt={slot2Upsell.name}
                              className={`w-full h-full ${
                                secondUpsellCollapsed ? 'object-contain' : 'object-cover'
                              }`}
                              loading="lazy"
                            />
                          </div>
                          {/* Mobile: show details next to image - hidden when collapsed */}
                          {!secondUpsellCollapsed && (
                            <div className="sm:hidden flex-1">
                              <div className="inline-flex items-center gap-1.5 bg-[#FF8080]/15 dark:bg-[#FF8080]/20 text-[#FF8080] px-2 py-0.5 rounded-full text-[10px] font-medium mb-1">
                                <Gift className="w-2.5 h-2.5" />
                                Exclusive Deal Found!
                              </div>
                                <h3 className="text-base font-bold leading-tight">{slot2Upsell.headline}</h3>
                                <p className="text-sm font-semibold text-foreground/80">{slot2Upsell.name}</p>
                              </div>
                            )}
                            {!secondUpsellCollapsed && slot2Upsell.limitedQty && (
                              <div className="hidden sm:block mt-2 text-center text-xs text-orange-600 dark:text-orange-400 font-medium">
                                Only {slot2Upsell.limitedQty} left at this price
                            </div>
                          )}
                        </div>

                        {/* Product Details - adapts based on collapsed state */}
                        <div className="flex-1 min-w-0">
                          {/* Collapsed state: compact info with improved text contrast */}
                          {secondUpsellCollapsed ? (
                            <>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-sm sm:text-base text-foreground truncate">{slot2Upsell.badge} - {slot2Upsell.name}</span>
                                <span className="flex-shrink-0 inline-flex items-center gap-1 bg-green-500 text-white text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium">
                                  <Check className="w-3 h-3" />
                                  Secured
                                </span>
                              </div>
                                <p className="text-xs sm:text-sm text-muted-foreground truncate">{slot2Upsell.description.split('.')[0]}.</p>
                            </>
                          ) : (
                            <>
                              {/* Desktop header */}
                              <div className="hidden sm:block">
                                <div className="inline-flex items-center gap-2 bg-[#FF8080]/15 dark:bg-[#FF8080]/20 text-[#FF8080] px-2 py-1 rounded-full text-xs font-medium mb-2">
                                  <Gift className="w-3 h-3" />
                                  Exclusive Deal Found!
                                </div>
                                <h3 className="text-xl font-bold mb-1">{slot2Upsell.headline}</h3>
                                <p className="text-lg font-semibold text-foreground/80 mb-2">{slot2Upsell.name}</p>
                              </div>
                              <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">{slot2Upsell.description}</p>

                              {/* Why it makes sense - collapsible like first upsell */}
                              <button
                                onClick={() => setExpandedSecondDetails(!expandedSecondDetails)}
                                className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium text-[#0066CC] dark:text-[#f5c542] mb-3"
                              >
                                <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                Why this makes sense for you
                                {expandedSecondDetails ? <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                              </button>
                              
                              {expandedSecondDetails && (
                                <div className="bg-white/50 dark:bg-black/20 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4 space-y-1.5 sm:space-y-2">
                                    {slot2Upsell.whyItMakesSense.map((reason, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs sm:text-sm">
                                      <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                      <span>{reason}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}

                          {/* Price and Add Button - adapts based on collapsed state */}
                          <div className={`transition-all duration-400 ease-out ${
                            secondUpsellCollapsed 
                              ? 'hidden' 
                              : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'
                          }`}>
                            <div className="flex items-center sm:block gap-3">
                              <div className="flex items-baseline gap-1.5 sm:gap-2">
                                <span className="text-xl sm:text-2xl font-bold text-green-600">{fp(slot2Display.price)}</span>
                                <span className="text-sm sm:text-lg text-muted-foreground line-through">{fp(slot2Display.originalPrice)}</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#22c55e]/15 text-[#22c55e]">-{slot2Upsell.savingsPercent}%</span>
                              </div>
                              <div className="text-xs sm:text-sm text-green-600 font-medium">
                                You save {fp(slot2Display.savings)}
                              </div>
                            </div>

                            <button
                              onClick={(e) => {
                                // Prevent double-click during animation
                                if (isSecondUpsellAnimating) return;
                                
                                if (!selectedSecondUpsell) {
                                  // Lock button during animation
                                  setIsSecondUpsellAnimating(true);
                                  
                                  // Accept upsell - trigger collapse animation with confetti
                                  setSelectedSecondUpsell(true);
                                  
                                  // GA4: Track select_promotion and add_to_cart
                                  trackUpsell2Add();
                                  
                                  setTimeout(() => {
                                    setSecondUpsellCollapsed(true);
                                    // Show third upsell with reveal animation after collapse
                                    if (!hideThirdUpsell) {
                                      setTimeout(() => {
                                        setShowThirdUpsell(true);
                                        setTimeout(() => setThirdUpsellRevealing(true), 50);
                                      }, 400);
                                    }
                                    // Unlock button after animation completes
                                    setTimeout(() => setIsSecondUpsellAnimating(false), 500);
                                  }, 600);
                                } else {
                                  // Lock button during animation
                                  setIsSecondUpsellAnimating(true);
                                  
                                  // Remove upsell - expand back
                                  setSecondUpsellCollapsed(false);
                                  setSelectedSecondUpsell(false);
                                  
                                  // GA4: Track remove_from_cart
                                  trackUpsell2Remove();
                                  
                                  // Unlock button after animation completes
                                  setTimeout(() => setIsSecondUpsellAnimating(false), 600);
                                }
                              }}
                              className={`w-full sm:w-auto px-6 py-3 rounded-full font-semibold transition-all text-sm sm:text-base ${
                                selectedSecondUpsell
                                  ? "bg-green-500 text-white"
                                  : "bg-[#007AFF] text-white hover:bg-[#d32f2f]"
                              }`}
                            >
                              {selectedSecondUpsell ? (
                                <span className="flex items-center justify-center gap-2">
                                  <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                                  Added!
                                </span>
                              ) : (
                                "Add to Order"
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Price info and remove button - only visible when collapsed */}
                        {secondUpsellCollapsed && (
                          <div className="flex-shrink-0 flex items-center gap-2 sm:gap-3">
                            <div className="text-right">
                              <div className="flex items-baseline gap-1">
                                <span className="text-sm sm:text-base font-bold text-green-600">{fp(slot2Display.price)}</span>
                                <span className="text-[10px] sm:text-xs text-muted-foreground line-through">{fp(slot2Display.originalPrice)}</span>
                              </div>
                              <div className="text-[9px] sm:text-[10px] text-green-600 font-medium">
                                Save {fp(slot2Display.savings)}
                              </div>
                            </div>
                            {/* Remove button */}
                            <button
                              onClick={() => {
                                setSecondUpsellCollapsed(false);
                                setSelectedSecondUpsell(false);
                                
                                // GA4: Track remove_from_cart
                                trackUpsell2Remove();
                              }}
                              className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
                              title="Remove from order"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Third Upsell removed — single upsell flow only */}
              {false && dealSearchComplete && showThirdUpsell && !hideThirdUpsell && (
                <div className={`relative rounded-2xl border overflow-hidden mb-4 transition-all duration-500 ease-out ${
                    thirdUpsellRevealing 
                      ? "opacity-100 translate-y-0 max-h-[800px]" 
                      : "opacity-0 -translate-y-4 max-h-0"
                  } ${
                    selectedThirdUpsell 
                      ? "bg-white dark:bg-[#0a1628]/30 border-green-500" 
                      : "bg-gradient-to-br from-[#f0f7ff] to-[#e8f4f8] dark:from-[#0a1628]/50 dark:to-[#003d7a]/50 border-[#003d7a]/30"
                  }`}
                  style={{ transitionProperty: 'opacity, transform, max-height, background-color, border-color' }}
                >
                  {/* Badge with timer */}
                  {slot3Upsell.badge && (
                    <div className={`absolute top-0 left-0 right-0 z-10 text-white text-[10px] sm:text-xs font-bold py-1.5 sm:py-2 px-3 sm:px-4 flex items-center justify-between transition-all duration-400 ease-out ${
                      selectedThirdUpsell 
                        ? "bg-gradient-to-r from-green-600 to-emerald-600" 
                        : "bg-gradient-to-r from-[#0066CC] to-[#003d7a]"
                      }`}>
                        <span className="truncate">
                          {selectedThirdUpsell ? 'ADDED TO CART' : `${slot3Upsell.badge} - ${slot3Upsell.savingsPercent}% OFF`}
                      </span>
                      <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
                        <span className="hidden sm:inline text-white/80 font-normal">OFFER RESERVED FOR</span>
                        <div className="flex items-center gap-0.5 sm:gap-1 font-mono">
                          <span className="bg-white/20 px-1 sm:px-1.5 py-0.5 rounded text-[10px] sm:text-xs">{String(dealTimer.minutes).padStart(2, '0')}</span>
                          <span className="text-[#f5c542]">:</span>
                          <span className="bg-white/20 px-1 sm:px-1.5 py-0.5 rounded text-[10px] sm:text-xs">{String(dealTimer.seconds).padStart(2, '0')}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Single container with smooth resize animation — 1:1 with second upsell */}
                  <div 
                    className="overflow-hidden"
                    style={{
                      maxHeight: thirdUpsellCollapsed ? '120px' : '800px',
                      background: 'transparent',
                      transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), background 0.5s ease-out',
                    }}
                  >
                    <div className={`${
                      thirdUpsellCollapsed 
                        ? 'p-3 sm:p-4 pt-10 sm:pt-12' 
                        : 'p-4 sm:p-5 pt-12 sm:pt-14'
                    }`}
                    style={{ transition: 'padding 0.3s ease-out' }}>
                      <div className={`flex items-start ${
                        thirdUpsellCollapsed 
                          ? 'flex-row gap-3 sm:gap-4' 
                          : 'flex-col sm:flex-row gap-4 sm:gap-5'
                      }`}
                      style={{ transition: 'gap 0.3s ease-out' }}>
                        {/* Product Image - resizes based on collapsed state */}
                        <div className={`flex-shrink-0 ${
                          thirdUpsellCollapsed 
                            ? 'w-14 h-14 sm:w-16 sm:h-16' 
                            : 'flex gap-4 sm:block'
                        }`}>
                          <div className={`overflow-hidden ${
                            thirdUpsellCollapsed 
                              ? 'w-14 h-14 sm:w-16 sm:h-16 bg-[#f5f0e6] rounded' 
                              : 'w-24 h-24 sm:w-40 sm:h-40 bg-[#f5f0e6] rounded-lg'
                          }`}>
                            <img 
                              src={slot3Upsell.image}
                              alt={slot3Upsell.name}
                              className={`w-full h-full ${
                                thirdUpsellCollapsed ? 'object-contain' : 'object-cover'
                              }`}
                              loading="lazy"
                            />
                          </div>
                          {/* Mobile: show details next to image - hidden when collapsed */}
                          {!thirdUpsellCollapsed && (
                            <div className="sm:hidden flex-1">
                              <div className="inline-flex items-center gap-1.5 bg-[#FF8080]/15 dark:bg-[#FF8080]/20 text-[#FF8080] px-2 py-0.5 rounded-full text-[10px] font-medium mb-1">
                                <Gift className="w-2.5 h-2.5" />
                                Exclusive Deal Found!
                              </div>
                              <h3 className="text-base font-bold leading-tight">{slot3Upsell.headline}</h3>
                              <p className="text-sm font-semibold text-foreground/80">{slot3Upsell.name}</p>
                            </div>
                          )}
                          {!thirdUpsellCollapsed && slot3Upsell.limitedQty && (
                            <div className="hidden sm:block mt-2 text-center text-xs text-orange-600 dark:text-orange-400 font-medium">
                              Only {slot3Upsell.limitedQty} left at this price
                            </div>
                          )}
                        </div>

                        {/* Product Details - adapts based on collapsed state */}
                        <div className="flex-1 min-w-0">
                          {/* Collapsed state: compact info */}
                          {thirdUpsellCollapsed ? (
                            <>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-bold text-sm sm:text-base text-foreground truncate">{slot3Upsell.badge} - {slot3Upsell.name}</span>
                                <span className="flex-shrink-0 inline-flex items-center gap-1 bg-green-500 text-white text-[10px] sm:text-xs px-2 py-0.5 rounded-full font-medium">
                                  <Check className="w-3 h-3" />
                                  Secured
                                </span>
                              </div>
                              <p className="text-xs sm:text-sm text-muted-foreground truncate">{slot3Upsell.description.split('.')[0]}.</p>
                            </>
                          ) : (
                            <>
                              {/* Desktop header */}
                              <div className="hidden sm:block">
                                <div className="inline-flex items-center gap-2 bg-[#FF8080]/15 dark:bg-[#FF8080]/20 text-[#FF8080] px-2 py-1 rounded-full text-xs font-medium mb-2">
                                  <Gift className="w-3 h-3" />
                                  Exclusive Deal Found!
                                </div>
                                <h3 className="text-xl font-bold mb-1">{slot3Upsell.headline}</h3>
                                <p className="text-lg font-semibold text-foreground/80 mb-2">{slot3Upsell.name}</p>
                              </div>
                              <p className="text-sm sm:text-base text-muted-foreground mb-3 sm:mb-4">{slot3Upsell.description}</p>

                              {/* Why it makes sense - collapsible */}
                              <button
                                onClick={() => setExpandedThirdDetails(!expandedThirdDetails)}
                                className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-medium text-[#0066CC] dark:text-[#f5c542] mb-3"
                              >
                                <Info className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                Why this makes sense for you
                                {expandedThirdDetails ? <ChevronUp className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> : <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
                              </button>
                              
                              {expandedThirdDetails && (
                                <div className="bg-white/50 dark:bg-black/20 rounded-lg p-2.5 sm:p-3 mb-3 sm:mb-4 space-y-1.5 sm:space-y-2">
                                  {slot3Upsell.whyItMakesSense.map((reason, i) => (
                                    <div key={i} className="flex items-start gap-2 text-xs sm:text-sm">
                                      <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                      <span>{reason}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </>
                          )}

                          {/* Price and Add Button - adapts based on collapsed state */}
                          <div className={`transition-all duration-400 ease-out ${
                            thirdUpsellCollapsed 
                              ? 'hidden' 
                              : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'
                          }`}>
                            <div className="flex items-center sm:block gap-3">
                              <div className="flex items-baseline gap-1.5 sm:gap-2">
                                <span className="text-xl sm:text-2xl font-bold text-green-600">{fp(slot3Upsell.price)}</span>
                                <span className="text-sm sm:text-lg text-muted-foreground line-through">{fp(slot3Upsell.originalPrice)}</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-[#22c55e]/15 text-[#22c55e]">-{slot3Upsell.savingsPercent}%</span>
                              </div>
                              <div className="text-xs sm:text-sm text-green-600 font-medium">
                                You save {fp(slot3Upsell.savings)}
                              </div>
                            </div>

                            <button
                              onClick={(e) => {
                                if (isThirdUpsellAnimating) return;
                                
                                if (!selectedThirdUpsell) {
                                  setIsThirdUpsellAnimating(true);
                                  setSelectedThirdUpsell(true);
                                  trackUpsell3Add();
                                  
                                  setTimeout(() => {
                                    setThirdUpsellCollapsed(true);
                                    setTimeout(() => setIsThirdUpsellAnimating(false), 500);
                                  }, 600);
                                } else {
                                  setIsThirdUpsellAnimating(true);
                                  setThirdUpsellCollapsed(false);
                                  setSelectedThirdUpsell(false);
                                  trackUpsell3Remove();
                                  setTimeout(() => setIsThirdUpsellAnimating(false), 600);
                                }
                              }}
                              className={`w-full sm:w-auto px-6 py-3 rounded-full font-semibold transition-all text-sm sm:text-base ${
                                selectedThirdUpsell
                                  ? "bg-green-500 text-white"
                                  : "bg-[#007AFF] text-white hover:bg-[#d32f2f]"
                              }`}
                            >
                              {selectedThirdUpsell ? (
                                <span className="flex items-center justify-center gap-2">
                                  <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                                  Added!
                                </span>
                              ) : (
                                "Add to Order"
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Price info and remove button - only visible when collapsed */}
                        {thirdUpsellCollapsed && (
                          <div className="flex-shrink-0 flex items-center gap-2 sm:gap-3">
                            <div className="text-right">
                              <div className="flex items-baseline gap-1">
                                <span className="text-sm sm:text-base font-bold text-green-600">{fp(slot3Upsell.price)}</span>
                                <span className="text-[10px] sm:text-xs text-muted-foreground line-through">{fp(slot3Upsell.originalPrice)}</span>
                              </div>
                              <div className="text-[9px] sm:text-[10px] text-green-600 font-medium">
                                Save {fp(slot3Upsell.savings)}
                              </div>
                            </div>
                            {/* Remove button */}
                            <button
                              onClick={() => {
                                setThirdUpsellCollapsed(false);
                                setSelectedThirdUpsell(false);
                                trackUpsell3Remove();
                              }}
                              className="p-1.5 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 transition-colors"
                              title="Remove from order"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Shipping Protection - Standalone Section Above Summary */}
              <div 
                className="bg-card border rounded-2xl p-4 sm:p-5 mb-4 cursor-pointer transition-all duration-200 hover:border-gray-300 dark:hover:border-gray-600"
                onClick={() => setShippingInsurance(!shippingInsurance)}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                    shippingInsurance 
                      ? 'bg-[#0066CC] border-[#0066CC]' 
                      : 'border-gray-400 dark:border-gray-500'
                  }`}>
                    {shippingInsurance && <Check className="w-3.5 h-3.5 text-white" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-[#0066CC] dark:text-gray-300" />
                        <span className="font-semibold text-sm sm:text-base">Shipping Protection</span>
                        <div className="relative group">
                          <Info className="w-3.5 h-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 p-2.5 bg-gray-900 dark:bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none">
                            <div className="font-semibold mb-1">What's covered:</div>
                            <ul className="space-y-0.5 text-gray-200">
                              <li>• Lost packages - full replacement</li>
                              <li>• Damaged items - free reshipping</li>
                              <li>• Stolen deliveries - covered</li>
                              <li>• Hassle-free claims process</li>
                            </ul>
                            <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900 dark:border-t-gray-800"></div>
                          </div>
                        </div>
                      </div>
                      <span className="font-bold">{fp(localeInsuranceCost)}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Protect your order against loss, damage & theft during shipping
                    </p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">Lost packages</span>
                      <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">Damaged items</span>
                      <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded-full">Stolen deliveries</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Total */}
              <div className="bg-secondary/50 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground inline-flex items-center text-xs sm:text-sm">Colagea Serum × {selectedQuantity}<span className="hidden sm:inline-flex items-center ml-1.5 text-[10px] bg-green-600 text-white px-1.5 py-0.5 rounded-full font-medium animate-discount-pulse" style={{ animationDelay: '0s' }}>-{selectedPricing.discount}%</span></span>
                  <div className="text-right text-xs sm:text-sm">
                    <span className="text-muted-foreground line-through mr-1.5 sm:mr-2">{fp(selectedPricing.originalPrice)}</span>
                    <span>{fp(selectedPricing.price)}</span>
                  </div>
                </div>
                {/* Free Welcome Pack included with all orders */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground inline-flex items-center text-xs sm:text-sm">
                    Welcome Pack
                  </span>
                  <div className="text-right text-xs sm:text-sm">
                    <span className="text-green-600 font-medium">FREE</span>
                  </div>
                </div>
                {/* Upsell: Extra Month */}
                {isPillowcaseSelected && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted-foreground inline-flex items-center text-xs sm:text-sm">{firstUpsell.name}<span className="hidden sm:inline-flex items-center ml-1.5 text-[10px] bg-green-600 text-white px-1.5 py-0.5 rounded-full font-medium animate-discount-pulse" style={{ animationDelay: '0.5s' }}>-{firstUpsell.savingsPercent}%</span></span>
                    <div className="text-right text-xs sm:text-sm">
                      <span className="text-muted-foreground line-through mr-1.5 sm:mr-2">{fp(firstUpsell.originalPrice)}</span>
                      <span>{fp(firstUpsell.price)}</span>
                    </div>
                  </div>
                )}
                {/* Second and third upsell line items removed — single upsell flow */}
                {/* Unlocked free gifts - positioned under upsells, same style as normal items */}
                {(() => {
                  const activeTiers = getActiveTiers(allGiftTiers, maxReachableTotal, isUS);
                  // Skip shipping tier (handled in shipping line below), only show product gifts
                  const productGifts = activeTiers.filter((t: { id: string; threshold: number; label: string; value: string; numericValue: number }) => t.id !== 'shipping' && giftCartTotal >= t.threshold);
                  return productGifts.length > 0 ? (
                    <>
                      {productGifts.map((gift: { id: string; threshold: number; label: string; value: string; numericValue: number }) => (
                        <div key={gift.id} className="flex items-center justify-between mb-2">
                          <span className="text-muted-foreground inline-flex items-center text-xs sm:text-sm">
                            {gift.label.replace('Free ', '')}
                            {gift.id === 'slippers' && (
                              <select
                                value={slipperSize}
                                onChange={(e) => { e.stopPropagation(); setSlipperSize(e.target.value as 'S/M' | 'L/XL'); }}
                                onClick={(e) => e.stopPropagation()}
                                className="ml-1.5 text-[10px] sm:text-xs text-muted-foreground bg-transparent border border-border rounded px-1 outline-none cursor-pointer appearance-none pr-3 py-0.5"
                                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right center' }}
                              >
                                <option value="S/M">S/M</option>
                                <option value="L/XL">L/XL</option>
                              </select>
                            )}
                          </span>
                          <div className="text-right text-xs sm:text-sm">
                            <span className="text-muted-foreground line-through mr-1.5 sm:mr-2">{gift.value}</span>
                            <span className="text-green-600 font-medium">FREE</span>
                          </div>
                        </div>
                      ))}
                    </>
                  ) : null;
                })()}
                {/* Shipping line - Free badge only for US visitors when cart >= shipping threshold */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-muted-foreground inline-flex items-center text-xs sm:text-sm">
                    Shipping
                  </span>
                  {isUS && giftCartTotal >= giftTiers.shipping.threshold ? (
                    <div className="text-right text-xs sm:text-sm">
                      <span className="text-muted-foreground line-through mr-1.5 sm:mr-2">{fp(giftTiers.shipping.value)}</span>
                      <span className="text-green-600 font-medium">FREE</span>
                    </div>
                  ) : (
                    <span className="font-medium text-xs sm:text-sm">{fp(isUS ? 0 : shippingCost)}</span>
                  )}
                </div>
                {shippingInsurance && (
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted-foreground text-xs sm:text-sm">
                      Shipping Protection
                    </span>
                    <span className="text-xs sm:text-sm">{fp(localeInsuranceCost)}</span>
                  </div>
                )}
                
                <div className="border-t border-border pt-3 mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold">Total</span>
                    <div className="text-right">
                      <span className="text-muted-foreground line-through text-sm mr-2">{fp(totalOriginalPrice)}</span>
                      <span className="text-xl font-bold">{fp(totalPrice)}</span>
                    </div>
                  </div>
                  <div className="bg-green-100 dark:bg-green-900/30 rounded-lg py-2 px-3 mt-2 flex items-center justify-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-400">
                      You're saving {fp(totalSavings)} on this order!
                    </span>
                  </div>
                </div>
              </div>

              {/* Dynamic location-based shipping confirmation */}
              <div className="mb-4">
                <ShippingMessage variant="checkout" />
              </div>

              {/* Proceed CTA */}
              <button
                onClick={handleProceedToCheckout}
                disabled={isCheckoutLoading}
                className={`w-full bg-[#007AFF] hover:bg-[#0066CC] text-white font-semibold py-4 rounded-full text-lg transition-colors flex items-center justify-center gap-2 ${isCheckoutLoading ? 'opacity-90 cursor-wait' : ''}`}
              >
                {isCheckoutLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Redirecting to Checkout...</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Proceed to Checkout
                  </>
                )}
              </button>

              {/* Payment gateway images */}
              <div className="flex items-center justify-center gap-2 mt-3">
                <img src="https://framerusercontent.com/images/bSFFCTtjkmIaGlqPy3PO43DkkGk.svg" alt="Visa" className="h-6" />
                <img src="https://framerusercontent.com/images/I7xg6lxhvJVKnwM1mN1dLJdEhZ0.svg" alt="Mastercard" className="h-6" />
                <img src="https://framerusercontent.com/images/jtf0I9r1qAZRg8faEptmYKSoa2c.svg" alt="Amex" className="h-6" />
                <img src="https://framerusercontent.com/images/ek75tbpQfCRJCnfpssG54DCS7dQ.svg" alt="Discover" className="h-6" />
                <img src="https://framerusercontent.com/images/FWqnr8Dbq9J8XTeq33tlleuWmlg.svg" alt="PayPal" className="h-6" />
                <img src="https://framerusercontent.com/images/GA26KH0kgksFTAmZKPUPQxI53k.svg" alt="Apple Pay" className="h-6" />
              </div>

              {/* Trust badges - SSL and Safe Checkout */}
              <div className="flex items-center justify-center gap-8 text-xs mt-3">
                <span className="flex items-center gap-1.5 uppercase tracking-wide text-[#004C99]">
                  <Lock className="w-4 h-4" />
                  <span className="flex flex-col leading-tight">
                    <span className="font-semibold">Secure</span>
                    <span>SSL Encryption</span>
                  </span>
                </span>
                <span className="flex items-center gap-1.5 uppercase tracking-wide text-[#004C99]">
                  <Shield className="w-4 h-4" />
                  <span className="flex flex-col leading-tight">
                    <span className="font-semibold">Guaranteed</span>
                    <span>Safe Checkout</span>
                  </span>
                </span>
              </div>

              {/* Stock & urgency bar - unified component */}
              <div className="mt-4">
                <StockInventoryBar stockLevel={stockLevel} countdown={countdown} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
